import React from 'react';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import BarChart from '../../../components/Charts/BarChart';
import ChartWrapper from '../../../components/Charts/ChartWrapper';
import DispatchVolumeTooltip from '../../../components/Charts/CustomToolTips/DispatchVolumeTooltip';
import DoughnutChart from '../../../components/Charts/DoughnutChart';
import PieChart from '../../../components/Charts/PieChart';
// import RouteAnalysisTooltip from '../../../components/Charts/CustomToolTips/RouteAnalysisTooltip';
import { useAppSelector } from '../../../redux/store';

const DashboardChart = () => {
  const theme = useTheme();
  const { dispatchVolumeData } = useAppSelector(
    (state) => state.dispatchVolumeData,
  );
  const issuesChartData = [
    { name: 'Interstate', value: 54, color: '#1F2130' },
    { name: 'Intrastate', value: 50, color: '#5B91F5' },
    { name: 'Rural', value: 50, color: '#006105' },
    { name: 'Long Haul', value: 50, color: '#FFCC00' },
  ];

  const performanceBenchmarkDistribution = [
    { name: 'Above Benchmark', value: 30, fill: '#5B91F5' },
    { name: 'Below Benchmark', value: 15, fill: '#CFDFFC' },
    { name: 'Meets Benchmark', value: 15, fill: '#FFC400' },
  ];

  const totalCompanies = performanceBenchmarkDistribution.reduce(
    (sum, item) => sum + item.value,
    0,
  );

  const hiringConflictDistribution = [
    { name: 'Conflicts', value: 30, fill: '#F87171' },
    { name: 'No conflicts', value: 15, fill: '#4ADE80' },
  ];

  const totalSuccessfulHires = hiringConflictDistribution.reduce(
    (sum, item) => sum + item.value,
    0,
  );

  return (
    <Container>
      {/* Bar Chart */}
      <ChartWrapper label='Performance Benchmark Distribution' maxWidth='100%'>
        <BarChart
          xAxisDataKey='vehicleType'
          data={dispatchVolumeData}
          isAnimationActive={true}
          dataKeys={['totalVehicles', 'companiesUsing']}
          // strokes={['#5B91F5']}
          fills={[theme.palette.blueColor, theme.palette.lightBlueOpacity]}
          showLegend={false}
          aspect={1.2}
          externalTooltip={{ content: <DispatchVolumeTooltip /> }}
        />
      </ChartWrapper>

      {/* Bar Chart */}
      {/* <ChartWrapper label='Route Analysis Chart' maxWidth='100%'>
        <BarChart
          xAxisDataKey='route'
          data={routeAnalysisData}
          isAnimationActive={true}
          dataKeys={['companyCount']}
          // fills={['#5B91F5']}
          fills={[theme.palette.blueColor, theme.palette.lightBlueOpacity]}
          showLegend={false}
          // yLabel='Average Tier Score per month'
          externalTooltip={{ content: <RouteAnalysisTooltip /> }}
        />
      </ChartWrapper> */}

      <PieChartArea>
        {/* Doughnut chart 1 */}
        <ChartWrapper
          label='Performance Benchmark Distribution'
          maxWidth='100%'
          radius='8px'
        >
          <ChartRow>
            <DonutArea>
              <DoughnutChart
                data={performanceBenchmarkDistribution}
                showLegend={false}
                tooltipLabel='Vehicle Status'
                centerLabel='Total Companies'
                centerValue={totalCompanies}
                aspect={1}
                innerRadius='72%'
                outerRadius='84%'
              />
            </DonutArea>
            <LegendArea>
              {performanceBenchmarkDistribution.map((item) => (
                <LegendRow key={item.name}>
                  <LegendLabelBlock>
                    <LegendDot color={item.fill ?? '#5B91F5'} />
                    <LegendText>{item.name}</LegendText>
                  </LegendLabelBlock>
                  <LegendValue>{item.value}</LegendValue>
                </LegendRow>
              ))}
            </LegendArea>
          </ChartRow>
        </ChartWrapper>

        {/* Pie chart 1 */}
        <ChartWrapper label='Route Analysis Chart' maxWidth='100%' radius='8px'>
          <ChartRow>
            <PieArea>
              <PieChart
                data={issuesChartData}
                aspect={1}
                outerRadius={96}
                cx='50%'
                cy='50%'
                showLabels={false}
                showLegend={false}
                tooltipValueFormatter={(value) => `${value}`}
              />
            </PieArea>
            <LegendArea>
              {issuesChartData.map((item) => (
                <LegendRow key={item.name}>
                  <LegendLabelBlock>
                    <LegendDot color={item.color ?? '#5B91F5'} />
                    <LegendText>{item.name}</LegendText>
                  </LegendLabelBlock>
                  <LegendValue>{item.value}</LegendValue>
                </LegendRow>
              ))}
            </LegendArea>
          </ChartRow>
        </ChartWrapper>

        {/* Doughnut chart 2 */}
        <ChartWrapper
          label='Hiring Conflict Distribution'
          maxWidth='100%'
          radius='8px'
        >
          <ChartRow>
            <DonutArea>
              <DoughnutChart
                data={hiringConflictDistribution}
                showLegend={false}
                tooltipLabel='Vehicle Status'
                // centerLabel='Total Successful Hires'
                centerLabel='Total Hires'
                centerValue={totalSuccessfulHires}
                aspect={1}
                innerRadius='72%'
                outerRadius='84%'
              />
            </DonutArea>
            <LegendArea>
              {hiringConflictDistribution.map((item) => (
                <LegendRow key={item.name}>
                  <LegendLabelBlock>
                    <LegendDot color={item.fill ?? '#5B91F5'} />
                    <LegendText>{item.name}</LegendText>
                  </LegendLabelBlock>
                  <LegendValue>{item.value}</LegendValue>
                </LegendRow>
              ))}
            </LegendArea>
          </ChartRow>
        </ChartWrapper>
      </PieChartArea>
    </Container>
  );
};

export default DashboardChart;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  min-height: 400px;

  /* border: 1px solid red; */
`;

const PieChartArea = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 884px) {
    grid-template-columns: 1fr;
    /* justify-items: center;
    gap: 14px; */
  }
`;

const DonutArea = styled.div`
  width: 100%;
  max-width: 240px;
  min-width: 220px;
  margin: 0 auto;
`;

const ChartRow = styled.div`
  height: 100%;
  display: grid;
  grid-template-columns: minmax(190px, 260px) 1fr;
  align-items: center;
  gap: 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 14px;
  }
`;

const LegendArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const PieArea = styled.div`
  width: 100%;
  max-width: 240px;
  min-width: 220px;
  margin: 0 auto;
`;

const LegendRow = styled.div`
  display: grid;
  grid-template-columns: 140px auto;
  gap: 16px;
`;

const LegendLabelBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LegendDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

const LegendText = styled.span`
  font-size: 14px;
  line-height: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.neutralGray};
`;

const LegendValue = styled.span`
  font-size: 14px;
  line-height: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.logirateBlack};
`;
